/**
 * 360度カメラ画像 (全天球 / 正距円筒図法) の判定 (docs/27 §3)。
 *
 * 判定は **加工前の元ファイル** に対して行う。投稿前処理 (`preprocessPhoto`、canvas 再エンコード) で
 * EXIF / XMP は消えるため、判定結果は Firestore の `post.photo` に保存して表示層が参照する。
 *
 * 判定順:
 *   1. JPEG の APP1 XMP セグメントにある Photo Sphere XMP (`GPano:ProjectionType="equirectangular"`)。
 *      RICOH THETA / Insta360 / Google カメラ等の 360 カメラ・アプリはこれを書く。
 *      `GPano:PoseHeadingDegrees` (画像中央列の方位) があれば併せて読む。
 *   2. XMP が無い場合は縦横比 2:1 (±2%) かつ幅 2000px 以上なら全天球とみなす (`detectedBy: 'aspect'`)。
 *      通常のパノラマモード写真は 2:1 より横長になることが多く、誤判定は限定的。呼出側 UI で
 *      「360°として扱う」を利用者が切り替えられるようにする前提の緩い判定。
 */

export type PhotoProjection = 'flat' | 'equirectangular';

export interface PanoramaDetection {
  projection: PhotoProjection;
  /** 判定根拠。flat のときは null。 */
  detectedBy: 'xmp' | 'aspect' | null;
  /** XMP GPano:PoseHeadingDegrees (画像中央の方位、0〜360)。無ければ null。 */
  poseHeadingDeg: number | null;
  width: number | null;
  height: number | null;
}

const XMP_HEADER = 'http://ns.adobe.com/xap/1.0/\0';
/** XMP APP1 は先頭付近にあるため、読む範囲を限定する (巨大ファイル対策)。 */
const XMP_SCAN_BYTES = 512 * 1024;

export interface XmpGPano {
  projectionType: string | null;
  poseHeadingDeg: number | null;
  fullPanoWidthPixels: number | null;
  croppedAreaImageWidthPixels: number | null;
}

/** XMP パケット文字列から GPano 属性/要素を読む (属性形式・要素形式の両方に対応)。 */
export function parseGPanoFromXmp(xmp: string): XmpGPano {
  const read = (name: string): string | null => {
    const attr = new RegExp(`GPano:${name}\\s*=\\s*"([^"]*)"`).exec(xmp);
    if (attr) return attr[1] ?? null;
    const elem = new RegExp(`<GPano:${name}[^>]*>([^<]*)</GPano:${name}>`).exec(xmp);
    return elem ? (elem[1] ?? null) : null;
  };
  const num = (s: string | null): number | null => {
    if (s === null || s.trim() === '') return null;
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
  };
  return {
    projectionType: read('ProjectionType')?.trim().toLowerCase() ?? null,
    poseHeadingDeg: num(read('PoseHeadingDegrees')),
    fullPanoWidthPixels: num(read('FullPanoWidthPixels')),
    croppedAreaImageWidthPixels: num(read('CroppedAreaImageWidthPixels')),
  };
}

/**
 * JPEG バイト列の先頭から APP1 XMP セグメントを探して XMP パケット文字列を返す (無ければ null)。
 * JPEG 以外 (HEIC/PNG 等) は null。
 */
export function extractXmpFromJpeg(bytes: Uint8Array): string | null {
  if (bytes.length < 4 || bytes[0] !== 0xff || bytes[1] !== 0xd8) return null;
  let offset = 2;
  const headerBytes = new TextEncoder().encode(XMP_HEADER);
  while (offset + 4 <= bytes.length) {
    if (bytes[offset] !== 0xff) return null;
    const marker = bytes[offset + 1];
    if (marker === 0xda || marker === 0xd9) return null; // SOS / EOI: 以降に APP1 は無い
    const length = ((bytes[offset + 2] ?? 0) << 8) | (bytes[offset + 3] ?? 0);
    if (length < 2) return null;
    if (marker === 0xe1) {
      const payloadStart = offset + 4;
      const payloadEnd = Math.min(bytes.length, offset + 2 + length);
      let matches = payloadEnd - payloadStart >= headerBytes.length;
      for (let i = 0; matches && i < headerBytes.length; i++) {
        if (bytes[payloadStart + i] !== headerBytes[i]) matches = false;
      }
      if (matches) {
        return new TextDecoder('utf-8').decode(
          bytes.subarray(payloadStart + headerBytes.length, payloadEnd),
        );
      }
    }
    offset += 2 + length;
  }
  return null;
}

/** 幅・高さから 2:1 の全天球らしさを判定する (pure)。 */
export function looksEquirectangularByAspect(width: number, height: number): boolean {
  if (!(width > 0) || !(height > 0)) return false;
  const ratio = width / height;
  return width >= 2000 && Math.abs(ratio - 2) <= 0.04;
}

/** XMP の GPano から判定する (pure)。切り出し (部分パノラマ) は全天球扱いにしない。 */
export function projectionFromGPano(g: XmpGPano): PhotoProjection {
  if (g.projectionType !== 'equirectangular') return 'flat';
  if (
    g.fullPanoWidthPixels !== null &&
    g.croppedAreaImageWidthPixels !== null &&
    g.fullPanoWidthPixels > 0 &&
    g.croppedAreaImageWidthPixels / g.fullPanoWidthPixels < 0.95
  ) {
    return 'flat';
  }
  return 'equirectangular';
}

async function readImageSize(file: Blob): Promise<{ width: number; height: number } | null> {
  if (typeof createImageBitmap === 'function') {
    try {
      const bmp = await createImageBitmap(file);
      const size = { width: bmp.width, height: bmp.height };
      bmp.close();
      return size;
    } catch {
      /* fallthrough (HEIC 等のデコード不可) */
    }
  }
  if (typeof Image === 'undefined' || typeof URL === 'undefined') return null;
  return await new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    img.src = url;
  });
}

/**
 * 元ファイルから 360度 (全天球) 画像かどうかを判定する。失敗しても throw せず `flat` を返す。
 */
export async function detectPanorama(file: Blob): Promise<PanoramaDetection> {
  let gpano: XmpGPano | null = null;
  try {
    const head = new Uint8Array(await file.slice(0, XMP_SCAN_BYTES).arrayBuffer());
    const xmp = extractXmpFromJpeg(head);
    if (xmp) gpano = parseGPanoFromXmp(xmp);
  } catch {
    gpano = null;
  }
  const size = await readImageSize(file);
  const width = size?.width ?? null;
  const height = size?.height ?? null;

  if (gpano && projectionFromGPano(gpano) === 'equirectangular') {
    return {
      projection: 'equirectangular',
      detectedBy: 'xmp',
      poseHeadingDeg: normalizeHeading(gpano.poseHeadingDeg),
      width,
      height,
    };
  }
  if (width !== null && height !== null && looksEquirectangularByAspect(width, height)) {
    return {
      projection: 'equirectangular',
      detectedBy: 'aspect',
      poseHeadingDeg: normalizeHeading(gpano?.poseHeadingDeg ?? null),
      width,
      height,
    };
  }
  return { projection: 'flat', detectedBy: null, poseHeadingDeg: null, width, height };
}

function normalizeHeading(deg: number | null): number | null {
  if (deg === null || !Number.isFinite(deg)) return null;
  return ((deg % 360) + 360) % 360;
}
